import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { X, Lock, Mail, User, AlertCircle, CheckCircle, Loader2 } from 'lucide-react'

export type AuthMode = 'signin' | 'signup' | 'forgot'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialMode?: AuthMode
}

export function mapAuthError(msg: string): string {
  if (!msg) return 'Terjadi kesalahan saat otentikasi.'
  const lower = msg.toLowerCase()
  if (lower.includes('invalid login credentials') || lower.includes('invalid credentials')) {
    return 'Email atau kata sandi tidak cocok. Silakan periksa kembali.'
  }
  if (lower.includes('email not confirmed') || lower.includes('not confirmed')) {
    return 'Email Anda belum dikonfirmasi. Silakan periksa kotak masuk email Anda.'
  }
  if (lower.includes('already registered') || lower.includes('user already exists')) {
    return 'Email ini sudah terdaftar. Silakan masuk menggunakan kata sandi Anda atau gunakan opsi lupa kata sandi.'
  }
  if (lower.includes('rate limit')) {
    return 'Terlalu banyak permintaan. Silakan tunggu beberapa saat lagi.'
  }
  if (lower.includes('at least 6 characters') || lower.includes('password should be')) {
    return 'Kata sandi harus minimal 6 karakter.'
  }
  return msg
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialMode = 'signin',
}) => {
  const [mode, setMode] = useState<AuthMode>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode)
      setEmail('')
      setPassword('')
      setDisplayName('')
      setErrorMsg(null)
      setSuccessMsg(null)
    }
  }, [isOpen, initialMode])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)
    setSuccessMsg(null)
    setLoading(true)

    try {
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password: password,
          options: {
            data: {
              display_name: displayName.trim() || email.split('@')[0],
            },
          },
        })

        if (error) {
          setErrorMsg(mapAuthError(error.message))
          return
        }

        // Check for duplicate registration:
        // Supabase returns user with identities = [] when email enumeration protection is ON
        const isDuplicate = Boolean(
          data?.user && (!data.user.identities || data.user.identities.length === 0)
        )

        if (isDuplicate) {
          setErrorMsg(
            'Email ini sudah terdaftar. Silakan masuk dengan kata sandi Anda atau gunakan opsi lupa kata sandi.'
          )
          setMode('signin')
          return
        }

        if (data?.session) {
          setSuccessMsg('Akun berhasil dibuat dan Anda telah masuk!')
          setTimeout(() => {
            onSuccess()
            onClose()
          }, 800)
        } else {
          // If session was not immediately returned (e.g. email confirmation required)
          const { error: signInErr } = await supabase.auth.signInWithPassword({
            email: email.trim(),
            password: password,
          })

          if (signInErr) {
            if (signInErr.message.toLowerCase().includes('confirm')) {
              setSuccessMsg(
                'Pendaftaran berhasil! Tautan konfirmasi telah dikirim ke email Anda. Silakan verifikasi email Anda sebelum masuk.'
              )
              setMode('signin')
            } else {
              setErrorMsg(mapAuthError(signInErr.message))
              setMode('signin')
            }
          } else {
            setSuccessMsg('Akun berhasil dibuat dan masuk!')
            setTimeout(() => {
              onSuccess()
              onClose()
            }, 800)
          }
        }
      } else if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password: password,
        })

        if (error) {
          setErrorMsg(mapAuthError(error.message))
          return
        }

        setSuccessMsg('Berhasil masuk!')
        setTimeout(() => {
          onSuccess()
          onClose()
        }, 600)
      } else if (mode === 'forgot') {
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: window.location.origin,
        })

        if (error) {
          setErrorMsg(mapAuthError(error.message))
          return
        }

        setSuccessMsg(
          'Tautan pemulihan kata sandi telah dikirim ke email Anda. Silakan periksa kotak masuk Anda.'
        )
      }
    } catch (err: any) {
      setErrorMsg(mapAuthError(err.message || 'Terjadi kesalahan saat otentikasi'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="relative w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl border border-slate-100 space-y-5 animate-in zoom-in-95 duration-200">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          aria-label="Tutup"
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div className="text-left space-y-1">
          <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-teal-50 text-[#0F766E] border border-teal-200/60 text-[11px] font-semibold">
            {mode === 'signup'
              ? 'Buat Akun BekalNanti'
              : mode === 'forgot'
              ? 'Pemulihan Akun'
              : 'Masuk ke Akun Anda'}
          </div>
          <h3 className="text-xl font-bold text-slate-900">
            {mode === 'signup'
              ? 'Mulai Simpan Bekal Anda'
              : mode === 'forgot'
              ? 'Lupa Kata Sandi?'
              : 'Lanjutkan Perjalanan'}
          </h3>
          <p className="text-xs text-slate-500">
            {mode === 'signup'
              ? 'Simpan dan pantau perkembangan perencanaan keluarga Anda.'
              : mode === 'forgot'
              ? 'Masukkan email yang terdaftar untuk menerima tautan pemulihan kata sandi.'
              : 'Akses kembali perencanaan dan rencana yang telah Anda buat.'}
          </p>
        </div>

        {/* Alerts */}
        {errorMsg && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-start gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
        )}

        {successMsg && (
          <div className="p-3 rounded-xl bg-teal-50 border border-teal-200 text-[#0F766E] text-xs flex items-start gap-2">
            <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3.5 text-left">
          {mode === 'signup' && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Nama Lengkap
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <User size={16} />
                </div>
                <input
                  type="text"
                  required
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Contoh: Fajar Faezal"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <Mail size={16} />
              </div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@email.com"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
              />
            </div>
          </div>

          {mode !== 'forgot' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-slate-700">
                  Kata Sandi
                </label>
                {mode === 'signin' && (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot')
                      setErrorMsg(null)
                      setSuccessMsg(null)
                    }}
                    className="text-[11px] font-semibold text-slate-500 hover:text-[#0EA5A4] transition-colors cursor-pointer"
                  >
                    Lupa kata sandi?
                  </button>
                )}
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Lock size={16} />
                </div>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimal 6 karakter"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Memproses...</span>
              </>
            ) : (
              <span>
                {mode === 'signup'
                  ? 'Daftar Sekarang'
                  : mode === 'forgot'
                  ? 'Kirim Tautan Pemulihan'
                  : 'Masuk'}
              </span>
            )}
          </button>
        </form>

        {/* Switch mode */}
        <div className="pt-2 border-t border-slate-100 text-center space-y-1">
          {mode === 'signup' ? (
            <p className="text-xs text-slate-500">
              Sudah punya akun?{' '}
              <button
                type="button"
                onClick={() => {
                  setMode('signin')
                  setErrorMsg(null)
                  setSuccessMsg(null)
                }}
                className="font-bold text-[#0EA5A4] hover:underline cursor-pointer"
              >
                Masuk di sini
              </button>
            </p>
          ) : mode === 'forgot' ? (
            <p className="text-xs text-slate-500">
              Ingat kata sandi Anda?{' '}
              <button
                type="button"
                onClick={() => {
                  setMode('signin')
                  setErrorMsg(null)
                  setSuccessMsg(null)
                }}
                className="font-bold text-[#0EA5A4] hover:underline cursor-pointer"
              >
                Masuk di sini
              </button>
            </p>
          ) : (
            <p className="text-xs text-slate-500">
              Belum punya akun?{' '}
              <button
                type="button"
                onClick={() => {
                  setMode('signup')
                  setErrorMsg(null)
                  setSuccessMsg(null)
                }}
                className="font-bold text-[#0EA5A4] hover:underline cursor-pointer"
              >
                Daftar sekarang
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
